// GYM-895: PWA install prompt (Add to Home Screen).
// Shown on mobile when the browser fires beforeinstallprompt.
// Dismissed state persisted to sessionStorage.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, X } from "lucide-react";

import { Button } from "@/components/ui/button";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function InstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem("pwa-install-dismissed");
    if (dismissed) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!visible || !deferredPrompt) return null;

  const handleInstall = async () => {
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    sessionStorage.setItem("pwa-install-dismissed", "1");
    setVisible(false);
  };

  return (
    <div className="fixed bottom-safe-or-4 inset-x-4 z-50 flex items-center justify-between gap-3 rounded-lg bg-primary-700 px-4 py-3 text-white shadow-lg md:hidden">
      <div className="flex items-center gap-2 min-w-0">
        <Download className="h-5 w-5 shrink-0" />
        <p className="text-sm font-medium truncate">{t("pwa.installPrompt")}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          variant="secondary"
          size="sm"
          className="min-h-[44px] min-w-[44px] text-xs bg-white/20 hover:bg-white/30 text-white border-0"
          onClick={handleInstall}
        >
          {t("pwa.install")}
        </Button>
        <button
          onClick={handleDismiss}
          className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-full hover:bg-white/20"
          aria-label={t("common.close")}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
